import React, { useState } from "react";
import { MdPushPin, MdExpandMore } from "react-icons/md";
import Card from "../../../../components/card";
import { NewsCategory, NewsFeedItem } from "../../../../interfaces/News";
import { categoryOf, NEWS_CATEGORIES, publishedLabel } from "../categories";

interface Props {
  items: NewsFeedItem[];
  filter: NewsCategory | null;
  onFilter: (c: NewsCategory | null) => void;
  onRead: (id: number) => void;
}

/**
 * O feed que todo colaborador vê. Abrir um comunicado marca como lido — é o gesto que o usuário
 * já faz, e pedir um clique a mais em "marcar como lido" só produziria badge que não zera.
 */
const NewsFeed: React.FC<Props> = ({ items, filter, onFilter, onRead }) => {
  const [openId, setOpenId] = useState<number | null>(null);

  const toggle = (item: NewsFeedItem) => {
    const opening = openId !== item.id;
    setOpenId(opening ? item.id : null);
    if (opening && !item.read) onRead(item.id);
  };

  const visible = filter === null ? items : items.filter((i) => i.category === filter);

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 p-10 text-center dark:border-white/10">
        <p className="font-display text-base font-bold text-navy-700 dark:text-white">
          Nenhum comunicado por aqui
        </p>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Quando alguém publicar no Opera/Data News, aparece aqui e no widget do seu notebook.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => onFilter(null)}
          className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${
            filter === null
              ? "bg-ink-900 text-white dark:bg-white dark:text-ink-900"
              : "bg-gray-100 text-gray-600 dark:bg-navy-700 dark:text-gray-300"
          }`}
        >
          Tudo
        </button>
        {NEWS_CATEGORIES.filter((meta) => items.some((i) => i.category === meta.value)).map(({ value: c }) => {
          const meta = categoryOf(c);
          return (
            <button
              key={c}
              type="button"
              onClick={() => onFilter(filter === c ? null : c)}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-all ${
                filter === c ? "bg-ink-900 text-white dark:bg-white dark:text-ink-900" : meta.chip
              }`}
            >
              {meta.label}
            </button>
          );
        })}
      </div>

      {visible.map((item) => {
        const meta = categoryOf(item.category);
        const open = openId === item.id;
        return (
          <Card key={item.id} extra="p-5">
            <button
              type="button"
              onClick={() => toggle(item)}
              className="flex w-full items-start gap-3 text-left"
              aria-expanded={open}
            >
              {/* Não lido nunca é só cor: o ponto vem acompanhado do texto "não lido". */}
              <span
                className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${
                  item.read ? "bg-transparent ring-1 ring-gray-300 dark:ring-white/20" : "dv-animated-gradient"
                }`}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.chip}`}>
                    {meta.label}
                  </span>
                  {item.pinned && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-600 dark:bg-navy-700 dark:text-gray-300">
                      <MdPushPin className="h-3 w-3" aria-hidden="true" /> Fixado
                    </span>
                  )}
                  {!item.read && (
                    <span className="text-[11px] font-semibold text-dv-blue-600 dark:text-dv-cyan-300">
                      não lido
                    </span>
                  )}
                </div>
                <h3 className="mt-1.5 font-display text-lg font-bold tracking-tight text-navy-700 dark:text-white">
                  {item.title}
                </h3>
                {item.summary && !open && (
                  <p className="mt-1 truncate text-sm text-gray-600 dark:text-gray-400">{item.summary}</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {publishedLabel(item.publishedAt)}
                  {item.createdByName ? ` · por ${item.createdByName}` : ""}
                </p>
              </div>
              <MdExpandMore
                className={`h-5 w-5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {open && (
              // Texto puro com quebras preservadas — o mesmo que o widget mostra. O comunicado
              // aparece para todo mundo exatamente como foi escrito, sem uma superfície
              // renderizando o que a outra mostra cru.
              <p className="mt-4 whitespace-pre-wrap border-t border-gray-100 pt-4 text-sm leading-relaxed text-gray-700 dark:border-white/10 dark:text-gray-300">
                {item.body}
              </p>
            )}
          </Card>
        );
      })}
    </div>
  );
};

export default NewsFeed;
